/** "Verplaatsen naar": pick the page (or the top level) to move a page into. */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CornerUpRight, FileText } from 'lucide-react';
import { useStore } from '../db/context';
import { searchCatalog, useCatalog } from '../state/catalog';
import { useUi } from '../state/ui';
import { Dialog } from './Dialog';
import { PageIcon } from './PageIcon';

export function MovePageDialog() {
  const { t } = useTranslation();
  const target = useUi((s) => s.moveTarget);
  const set = useUi((s) => s.set);
  return (
    <Dialog
      open={!!target}
      onOpenChange={(o) => !o && set({ moveTarget: null })}
      title={t('pageMenu.moveTitle')}
    >
      {target && <MoveBody pageId={target} onDone={() => set({ moveTarget: null })} />}
    </Dialog>
  );
}

function MoveBody({ pageId, onDone }: { pageId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const store = useStore();
  const byId = useCatalog((s) => s.byId);
  const pages = useCatalog((s) => s.pages);
  const [query, setQuery] = useState('');
  const page = byId.get(pageId);

  // The page itself and everything under it can't be the destination.
  const blocked = useMemo(() => {
    const out = new Set([pageId]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const p of pages)
        if (p.parentId && out.has(p.parentId) && !out.has(p.id)) {
          out.add(p.id);
          grew = true;
        }
    }
    return out;
  }, [pages, pageId]);
  const options = searchCatalog(query, 40)
    .filter((p) => !blocked.has(p.id))
    .slice(0, 12);

  const move = async (parentId: string | null) => {
    const siblings = (await store.listPages()).filter((p) => p.parentId === parentId);
    await store.movePage(pageId, parentId, siblings.at(-1)?.id ?? null);
    if (parentId) useUi.getState().toggleExpanded(parentId, true);
    onDone();
  };
  const row =
    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-bg-hover disabled:opacity-40';

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-fg-muted">
        {t('pageMenu.moveIntro', { title: page?.title || t('page.untitled') })}
      </p>
      <input
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && options[0]) void move(options[0].id);
        }}
        placeholder={t('pageMenu.moveSearch')}
        aria-label={t('pageMenu.moveSearch')}
        className="rounded-lg border border-border bg-bg px-2 py-1.5 text-sm outline-none focus:border-accent"
      />
      <ul className="flex max-h-80 flex-col overflow-y-auto" aria-label={t('pageMenu.moveTitle')}>
        <li>
          <button
            type="button"
            disabled={page?.parentId === null}
            onClick={() => void move(null)}
            className={row}
          >
            <CornerUpRight className="size-4 text-fg-muted" />
            {t('pageMenu.topLevel')}
          </button>
        </li>
        {options.map((p) => {
          const parent = p.parentId ? byId.get(p.parentId) : undefined;
          return (
            <li key={p.id}>
              <button
                type="button"
                disabled={p.id === page?.parentId}
                onClick={() => void move(p.id)}
                className={row}
              >
                <span className="grid size-4 place-items-center text-fg-muted">
                  <PageIcon icon={p.icon} fallback={<FileText className="size-4" />} />
                </span>
                <span className="truncate">{p.title || t('page.untitled')}</span>
                {parent && <span className="truncate text-xs text-fg-muted">{parent.title}</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
